import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Stack,
  Table,
  Button,
  Divider,
  Tooltip,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  LinearProgress,
  TableContainer,
} from '@mui/material';
// components
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
// utils
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// ---------------------------------------------------------------------------
// Attendance & Sessions grid for ONE batch of a diploma. Rows are students,
// columns are the batch's sessions; each cell is the student's Present / Late /
// Absent status for that session, followed by their P/L/A totals and their
// completion score. A batch selector switches cohorts; Export CSV downloads the
// same grid (name, email, every session's status, completion) from the backend.
//
// Attendance itself is MARKED per session in Batch Management — this is the
// read-only roll-up the user asked for on the Diploma Report.
// ---------------------------------------------------------------------------

type MatrixSession = { _id: string; title: string; scheduledAt: string | null };
type MatrixStudent = {
  _id: string;
  name: string;
  email: string;
  statuses: Record<string, 'present' | 'late' | 'absent'>;
  present: number;
  late: number;
  absent: number;
  attendancePct: number;
  quizAvg: number;
  completion: number;
};
type Matrix = {
  totalSessions: number;
  sessions: MatrixSession[];
  students: MatrixStudent[];
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error'> = {
  present: 'success',
  late: 'warning',
  absent: 'error',
};
const STATUS_SHORT: Record<string, string> = { present: 'P', late: 'L', absent: 'A' };

function fmtDate(v: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

type Props = { diplomaId: string; batchNames: string[] };

export default function AttendanceMatrixCard({ diplomaId, batchNames }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [batch, setBatch] = useState<string>(batchNames[0] || '');
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Keep a valid batch selected as the batch list resolves.
  useEffect(() => {
    if (!batch && batchNames.length) setBatch(batchNames[0]);
  }, [batchNames, batch]);

  useEffect(() => {
    if (!diplomaId || !batch) return;
    let alive = true;
    setLoading(true);
    axiosInstance
      .get(APIS.BATCH_SESSIONS.ATTENDANCE_MATRIX(diplomaId, batch))
      .then((res) => {
        if (!alive) return;
        setMatrix((res.data?.data ?? res.data) as Matrix);
      })
      .catch((e: any) => {
        if (!alive) return;
        setMatrix(null);
        enqueueSnackbar(e?.message || 'Failed to load attendance', { variant: 'error' });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [diplomaId, batch, enqueueSnackbar]);

  // Export via axiosInstance (blob) so the Bearer token is sent — a plain <a>
  // would hit the endpoint unauthenticated.
  const handleExport = async () => {
    if (!batch) return;
    setExporting(true);
    try {
      const res = await axiosInstance.get(
        APIS.BATCH_SESSIONS.ATTENDANCE_EXPORT_CSV(diplomaId, batch),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-${batch}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Export failed', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const sessions = matrix?.sessions ?? [];
  const students = matrix?.students ?? [];

  const hasBatches = batchNames.length > 0;
  const emptyMsg = useMemo(() => {
    if (!hasBatches) return 'This diploma has no batches yet.';
    if (loading) return '';
    if (!sessions.length) return 'No sessions scheduled for this batch yet.';
    if (!students.length) return 'No students enrolled in this batch yet.';
    return '';
  }, [hasBatches, loading, sessions.length, students.length]);

  return (
    <Card sx={{ mt: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        sx={{ p: 2, gap: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Attendance &amp; Sessions
        </Typography>
        {hasBatches && (
          <TextField
            select
            size="small"
            label="Batch"
            value={batch}
            onChange={(e) => setBatch(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {batchNames.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button
          variant="outlined"
          disabled={!students.length || exporting}
          startIcon={<Iconify icon="eva:download-outline" />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Stack>
      <Divider />

      {loading && <LinearProgress />}

      {emptyMsg ? (
        <Typography sx={{ p: 3, color: 'text.secondary' }}>{emptyMsg}</Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', minWidth: 200 }}>
                  Student
                </TableCell>
                {sessions.map((s, i) => (
                  <TableCell key={s._id} align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={`${s.title || `Session ${i + 1}`}${s.scheduledAt ? ` — ${fmtDate(s.scheduledAt)}` : ''}`}>
                      <span>{`S${i + 1}`}</span>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ color: 'success.main' }}>P</TableCell>
                <TableCell align="center" sx={{ color: 'warning.main' }}>L</TableCell>
                <TableCell align="center" sx={{ color: 'error.main' }}>A</TableCell>
                <TableCell align="center">Completion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((st) => (
                <TableRow key={st._id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" noWrap>
                      {st.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {st.email}
                    </Typography>
                  </TableCell>
                  {sessions.map((s) => {
                    const status = st.statuses[s._id] || 'absent';
                    return (
                      <TableCell key={s._id} align="center">
                        <Tooltip title={status[0].toUpperCase() + status.slice(1)}>
                          <Label color={STATUS_COLOR[status]} sx={{ minWidth: 24 }}>
                            {STATUS_SHORT[status]}
                          </Label>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">{st.present}</TableCell>
                  <TableCell align="center">{st.late}</TableCell>
                  <TableCell align="center">{st.absent}</TableCell>
                  <TableCell align="center" sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, st.completion)}
                        color={st.completion >= 50 ? 'success' : 'warning'}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 32 }}>
                        {st.completion}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}
